/**
 * EditProposalMessage — the chat "edit highlight" proposal, rendered
 * IN THE CHAT THREAD as Asthra's response message (tail block, newest
 * position) while the rewrite awaits the writer's decision.
 *
 * Thin adapter over ``ProposalCard`` — THE shared text-edit proposal
 * surface (also used by WriteApprovalMessage for agent-initiated
 * doc_write_section approvals). This module only maps the
 * /api/selection-edit proposal onto the card:
 *   - "Edit highlight" intent chip + header naming section+instruction
 *   - sanitized markdown preview (exactly what Approve will apply —
 *     the approve path runs the same sanitizer)
 *   - ambiguity / drift warning from ``occurrences_in_section``
 *     (ambiguity suppressed when the highlight was range-anchored)
 *
 * SRP: pure presentation. State machine + apply logic live in
 * useHighlightEditFlow; on decision the shell appends the durable
 * ``edit_proposal`` EventLog entry (this transient component then
 * unmounts and the projected message takes its place in history).
 */

import type React from "react";
import { useMemo } from "react";
import type { HighlightEditPhase, HighlightEditProposalState } from "../hooks/useHighlightEditFlow";
import { intentLabel } from "../intentLabels";
import { sanitizeDraftForWord } from "../services/draftSanitizer";
import ApplyProgress, { type ApplyStep } from "./ApplyProgress";
import ProposalCard from "./ProposalCard";
import TableDiffView from "./TableDiffView";

interface EditProposalMessageProps {
  proposal: HighlightEditProposalState;
  phase: HighlightEditPhase;
  /** Post-approve apply checklist from useHighlightEditFlow — non-empty
   *  only while the change is being applied. */
  applySteps: ApplyStep[];
  /** Live status line while a "Revise" re-run is in flight (phase ===
   *  "revising"). Null otherwise. */
  progressLabel?: string | null;
  onApprove: () => void;
  onReject: () => void;
  /** Re-run the rewrite against the current proposal + this instruction. */
  onRevise: (revisionInstruction: string) => void;
}

export const EditProposalMessage: React.FC<EditProposalMessageProps> = ({
  proposal,
  phase,
  applySteps,
  progressLabel = null,
  onApprove,
  onReject,
  onRevise,
}) => {
  const { request, response } = proposal;
  const sectionLabel = request.sectionNumber ? `§${request.sectionNumber}` : "the highlighted text";
  // After one or more revisions the header names the latest revision (what
  // the writer most recently asked); the original instruction still anchors
  // the audit trail via the recorded proposal.
  const activeInstruction = proposal.revisions.length
    ? proposal.revisions[proposal.revisions.length - 1]
    : request.instruction;
  const instructionPreview =
    activeInstruction.length > 80 ? `${activeInstruction.slice(0, 79)}…` : activeInstruction;
  const revisedSuffix = proposal.revisions.length
    ? ` · revised ×${proposal.revisions.length}`
    : "";

  // While applying, replace the (now non-actionable) card with the
  // stepped checklist so the writer sees the 15–30s Word render progress.
  if (phase === "applying" && applySteps.length > 0) {
    return (
      <ApplyProgress
        steps={applySteps}
        chipText={intentLabel("edit_highlight")}
        header={`Applying your change to ${sectionLabel}…`}
        ariaLabel={`Applying rewrite to ${sectionLabel}`}
      />
    );
  }

  // Preview exactly what Approve will apply — the approve path runs the
  // same sanitizer before writing to Word (useHighlightEditFlow).
  const previewMarkdown = useMemo(() => sanitizeDraftForWord(response.replacement_text), [response.replacement_text]);

  let warning: string | null = null;
  if (response.occurrences_in_section === 0 && response.section_draft) {
    warning =
      "The highlighted text wasn't found in the stored section draft — approving will update Word, but the backend record may not change.";
  } else if (response.occurrences_in_section > 1 && !proposal.anchored) {
    // Suppressed when the highlight was range-anchored at proposal
    // time — Approve then replaces the exact range, no ambiguity.
    warning = `This text appears ${response.occurrences_in_section} times in the section — the occurrence you highlighted (or the first match) will be updated.`;
  }

  const revising = phase === "revising";

  // Table proposal — render the snippet diff (changed cells highlighted)
  // instead of the markdown preview, and drop the Revise affordance (a
  // single-span refinement doesn't map onto a multi-row table edit).
  if (proposal.table) {
    const { snippet } = proposal.table;
    const changedRows = snippet.rows.filter((r) => !r.skippedReason && r.cells.some((c) => c.changed)).length;
    const tableWarning =
      snippet.skippedRows > 0
        ? `${snippet.skippedRows} row${snippet.skippedRows === 1 ? "" : "s"} couldn't be parsed as a table row and ${snippet.skippedRows === 1 ? "was" : "were"} left unchanged.`
        : null;
    return (
      <ProposalCard
        chipText={intentLabel("edit_highlight")}
        header={`Proposed table edits for ${sectionLabel} — "${instructionPreview}"`}
        previewMarkdown=""
        previewNode={<TableDiffView snippet={snippet} />}
        metaChips={[`${changedRows} row${changedRows === 1 ? "" : "s"}`, `${snippet.changedCells} cell${snippet.changedCells === 1 ? "" : "s"}`]}
        warning={tableWarning}
        busy={phase === "applying"}
        approveLabel="Apply changes"
        ariaLabel={`Table edit proposal for ${sectionLabel}`}
        onApprove={onApprove}
        onReject={onReject}
      />
    );
  }

  return (
    <ProposalCard
      chipText={intentLabel("edit_highlight")}
      header={`Proposed replacement for ${sectionLabel} — "${instructionPreview}"${revisedSuffix}`}
      previewMarkdown={previewMarkdown}
      originalText={response.original_text}
      warning={warning}
      busy={phase === "applying" || revising}
      statusLine={revising ? (progressLabel ?? "Revising the highlighted text…") : null}
      ariaLabel={`Rewrite proposal for ${sectionLabel}`}
      onApprove={onApprove}
      onReject={onReject}
      onRevise={onRevise}
    />
  );
};

export default EditProposalMessage;
